"use client";

import { FilterVariantCount } from "@/components/ui/filters";
import type { VariantFilterValue } from "@/lib/api/types";
import { formatEntityForms, type VariantField } from "@/lib/search/entity-variants";
import { titleCaseStrictSmallWords } from "@/lib/string/title-case";
import { cn } from "@/lib/utils";
import { FilterValueActions } from "../actions";
import { ScopeDropdown } from "./internal/scope-dropdown";
import { VariantsDropdown } from "./internal/variants-dropdown";

export function FilterJobTitleInput({
  title,
  fallbackScope,
  variantsField,
  onChange,
  onRemove,
}: {
  title: VariantFilterValue;
  /**
   * Top-level `filters.jt_filter_type` from the parent search. The chip falls
   * back to this when no per-item `scope` is set, mirroring the backend
   * builder which uses `jt_filter_type` as the default scope. Without this,
   * an agent that applies only `jt_filter_type: "c"` would still cause every
   * chip to render as "Current + Past".
   */
  fallbackScope?: "c" | "p" | "cp" | null;
  /** Opt-in: lets the chip carry alias variants matched from this field. */
  variantsField?: VariantField;
  onChange: (updated: VariantFilterValue) => void;
  onRemove: () => void;
}) {
  const name = titleCaseStrictSmallWords(title.text ?? "");
  const scope = (title.scope ?? fallbackScope ?? "cp") as "c" | "p" | "cp";
  const isExcluded = title.mode === "exclude";

  return (
    <div
      className={cn(
        "border rounded-md overflow-clip",
        isExcluded ? "border-destructive/30" : "border-border",
      )}
    >
      <div className="flex items-center justify-between bg-contrast/4 h-[34px] px-2">
        <div className="flex items-center gap-1.5 flex-1 min-w-0">
          <span
            title={title.variants?.length ? formatEntityForms({ text: name, variants: title.variants }) : undefined}
            className={cn(
              "text-[13px] font-normal truncate tracking-[-0.13px]",
              isExcluded
                ? "text-destructive/80 dark:text-red-400/80 line-through"
                : "text-foreground/80",
            )}
          >
            {name}
            <FilterVariantCount count={title.variants?.length ?? 0} />
          </span>
        </div>
        <FilterValueActions
          excluded={isExcluded}
          required={title.required ?? false}
          onToggleRequired={() =>
            onChange({ ...title, required: !title.required })
          }
          onToggleExclude={() =>
            onChange({ ...title, mode: isExcluded ? "include" : "exclude" })
          }
          onRemove={onRemove}
        />
      </div>
      <div className="flex items-center justify-end gap-3 bg-contrast/8 border-t border-border h-7 px-2">
        {variantsField && (
          <VariantsDropdown field={variantsField} item={title} onChange={onChange} />
        )}
        <ScopeDropdown
          value={scope}
          onChange={(v) => onChange({ ...title, scope: v })}
        />
      </div>
    </div>
  );
}
